import React, { useRef } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { 
  Box, 
  IconButton, 
  LinearProgress, 
  Typography, 
  Paper,
  Chip,
  Fade,
  Alert
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { alpha } from '@mui/material/styles';

import axios from '@/lib/v2/axios';

export interface UploadedFile {
  key: string;
  path: string;
  size: number;
  name: string;
  type: string;
}

export interface FileUpload {
  id: string;
  file: File;
  preview: string;
  progress: number;
  status: 'uploading' | 'success' | 'error';
  uploadedFile?: UploadedFile;
}

interface ImageUploaderProps {
  /**
   * Callback that returns the list of file uploads (including their updated statuses)
   * so the parent component (e.g., a form) can update its state or form values.
   */
  onUploadComplete: (fileUploads: FileUpload[]) => void;
  /**
   * Optional title for the upload area
   */
  title?: string;
  /**
   * Optional subtitle for the upload area
   */
  subtitle?: string;
  /**
   * Maximum number of files allowed
   */
  maxFiles?: number;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({ 
  onUploadComplete, 
  title = "Upload Files",
  subtitle = "Drag & drop files here, or click to browse",
  maxFiles = 10
}) => {
  const [fileUploads, setFileUploads] = React.useState<FileUpload[]>([]);
  const fileUploadsRef = useRef(fileUploads);

  // Update the ref whenever fileUploads changes
  React.useEffect(() => {
    fileUploadsRef.current = fileUploads;
  }, [fileUploads]);

  // This function simulates the file upload and returns the UploadedFile info.
  const simulateUpload = (file: File, onProgress: (progress: number) => void): Promise<UploadedFile> => {
    const formData = new FormData();
    formData.append('file', file);

    return axios
      .post('/api/v2/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent: any) => {
          if (progressEvent.loaded && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            onProgress(progress);
          }
        },
      })
      .then((response) => ({
        key: response.data.file_key,
        path: response.data.file_key,
        size: response.data.file_size,
        name: response.data.original_name,
        type: response.data.content_type,
      }));
  };

  // Dropzone onDrop callback
  const onDrop = React.useCallback(
    (acceptedFiles: File[]) => {
      // Check if adding these files would exceed the limit
      if (fileUploadsRef.current.length + acceptedFiles.length > maxFiles) {
        console.warn(`Cannot upload more than ${maxFiles} files`);
        return;
      }

      const uploadPromises = acceptedFiles.map((file) => {
        const alreadyAdded = fileUploadsRef.current.some(
          (upload) => upload.file.name === file.name && upload.file.size === file.size
        );
        if (alreadyAdded) return Promise.resolve(null);

        const newUpload: FileUpload = {
          id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
          file,
          preview: URL.createObjectURL(file),
          progress: 0,
          status: 'uploading',
        };
        setFileUploads((prev) => [...prev, newUpload]);

        return simulateUpload(file, (progress) => {
          setFileUploads((prev) =>
            prev.map((upload) => (upload.id === newUpload.id ? { ...upload, progress } : upload))
          );
        })
          .then((uploadedFile) => {
            console.log('File uploaded successfully:', uploadedFile);
            setFileUploads((prev) =>
              prev.map((upload) =>
                upload.id === newUpload.id ? { ...upload, progress: 100, status: 'success', uploadedFile } : upload
              )
            );
            return uploadedFile;
          })
          .catch((error) => {
            console.error('Error uploading file:', error);
            setFileUploads((prev) =>
              prev.map((upload) => (upload.id === newUpload.id ? { ...upload, status: 'error' } : upload))
            );
            throw error;
          });
      });

      Promise.all(uploadPromises)
        .then((results) => {
          // Instead of using the ref, get the latest state directly from the state update
          setFileUploads((currentUploads) => {
            const successfulUploads = currentUploads.filter((upload) => upload.status === 'success');
            console.log('Successful uploads in ImageUploader:', successfulUploads);
            onUploadComplete(successfulUploads);
            return currentUploads; // Return unchanged state
          });
        })
        .catch((err) => {
          console.error('File upload error:', err);
        });
    },
    [onUploadComplete, maxFiles]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    disabled: fileUploads.length >= maxFiles,
  });

  // Cleanup for object URLs
  React.useEffect(() => {
    return () => {
      fileUploads.forEach((upload) => { URL.revokeObjectURL(upload.preview); });
    };
  }, [fileUploads]);

  const handleRemoveFile = (id: string) => {
    setFileUploads((prev) => prev.filter((upload) => upload.id !== id));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))  } ${  sizes[i]}`;
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return '🖼️';
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
    if (fileType.includes('powerpoint') || fileType.includes('presentation')) return '📈';
    return '📎';
  };

  return (
    <Box>
      {/* Upload Area */}
      <Paper
        {...getRootProps()}
        elevation={isDragActive ? 8 : 1}
        sx={{
          border: `2px dashed ${isDragActive ? 'primary.main' : 'divider'}`,
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: fileUploads.length >= maxFiles ? 'not-allowed' : 'pointer',
          backgroundColor: isDragActive ? alpha('#1976d2', 0.04) : 'background.paper',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: fileUploads.length >= maxFiles ? 'divider' : 'primary.main',
            backgroundColor: fileUploads.length >= maxFiles ? 'background.paper' : alpha('#1976d2', 0.02),
          },
          opacity: fileUploads.length >= maxFiles ? 0.6 : 1,
        }}
      >
        <input {...getInputProps()} />
        <CloudUploadIcon 
          sx={{ 
            fontSize: 48, 
            color: isDragActive ? 'primary.main' : 'text.secondary',
            mb: 2 
          }} 
        />
        <Typography color={isDragActive ? 'primary.main' : 'text.primary'} gutterBottom variant="h6">
          {isDragActive ? 'Drop files here...' : title}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 1 }} variant="body2">
          {subtitle}
        </Typography>
        <Chip 
          color={fileUploads.length >= maxFiles ? 'error' : 'default'} 
          label={`${fileUploads.length}/${maxFiles} files`} 
          size="small"
          variant="outlined"
        />
      </Paper>

      {/* File List */}
      {fileUploads.length > 0 && (
        <Fade in>
          <Box sx={{ mt: 3 }}>
            <Typography gutterBottom variant="subtitle2">
              Uploaded Files ({fileUploads.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {fileUploads.map((upload) => (
                <Paper
                  elevation={2}
                  key={upload.id}
                  sx={{
                    position: 'relative',
                    width: 200,
                    overflow: 'hidden',
                    borderRadius: 2,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      elevation: 4,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  {/* Remove Button */}
                  <IconButton
                    onClick={() => { handleRemoveFile(upload.id); }}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 2,
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      backdropFilter: 'blur(4px)',
                      '&:hover': {
                        backgroundColor: 'error.main',
                        color: 'white',
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>

                  {/* File Preview */}
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    {upload.file.type.startsWith('image/') ? (
                      <Box
                        alt={upload.file.name}
                        component="img"
                        src={upload.preview}
                        sx={{
                          width: '100%',
                          height: 120,
                          objectFit: 'cover',
                          borderRadius: 1,
                          mb: 1,
                        }}
                      />
                    ) : (
                      <Box sx={{ mb: 1 }}>
                        <Typography sx={{ mb: 1 }} variant="h4">
                          {getFileIcon(upload.file.type)}
                        </Typography>
                      </Box>
                    )}

                    {/* File Info */}
                    <Typography 
                      sx={{ 
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        mb: 0.5
                      }} 
                      variant="body2"
                    >
                      {upload.file.name}
                    </Typography>
                    <Typography color="text.secondary" variant="caption">
                      {formatFileSize(upload.file.size)}
                    </Typography>

                    {/* Status */}
                    <Box sx={{ mt: 1 }}>
                      {upload.status === 'uploading' && (
                        <Box>
                          <LinearProgress 
                            sx={{ mb: 0.5 }} 
                            value={upload.progress} 
                            variant="determinate"
                          />
                          <Typography color="text.secondary" variant="caption">
                            {upload.progress}%
                          </Typography>
                        </Box>
                      )}
                      {upload.status === 'success' && (
                        <Chip
                          color="success"
                          icon={<CheckCircleOutlineIcon />}
                          label="Uploaded"
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {upload.status === 'error' && (
                        <Chip
                          color="error"
                          label="Error"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          </Box>
        </Fade>
      )}

      {/* Error Alert for Max Files */}
      {fileUploads.length >= maxFiles && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Maximum number of files ({maxFiles}) reached. Remove some files to upload more.
        </Alert>
      )}
    </Box>
  );
};

export default ImageUploader;
